import React, { useMemo } from 'react';

const W = 180;
const H = 54;
const GX = 44;
const GY = 70;
const PER_ROW = 4;
const PAD = 16;

const clip = (s, n) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '');

// Point where the line from rect center toward (tx,ty) leaves the rect.
function edge(cx, cy, tx, ty) {
  const dx = tx - cx;
  const dy = ty - cy;
  if (!dx && !dy) return { x: cx, y: cy };
  const s = Math.min((W / 2) / (Math.abs(dx) || 1e-9), (H / 2) / (Math.abs(dy) || 1e-9));
  return { x: cx + dx * s, y: cy + dy * s };
}

export default function TabMap({ members, aliases, aliasStatus = 'ok' }) {
  const layout = useMemo(() => {
    const memberAlias = new Map();
    members.forEach((m) => { if (m.richMenuAliasId) memberAlias.set(m.richMenuAliasId, m.id); });
    const known = new Set((aliases || []).map((a) => a.richMenuAliasId));

    const nodes = members.map((m, i) => ({
      key: m.id, kind: 'member', label: m.name, sub: m.richMenuAliasId, star: m.isDefault,
      col: i % PER_ROW, row: Math.floor(i / PER_ROW),
    }));
    const memberRows = Math.max(1, Math.ceil(members.length / PER_ROW));
    const byAlias = new Map(nodes.filter((n) => n.sub).map((n) => [n.sub, n]));
    const extras = [];
    const edges = [];
    const seen = new Set();
    members.forEach((m) => {
      (m.areas || []).forEach((a) => {
        const target = a?.action?.type === 'richmenuswitch' ? a.action.richMenuAliasId : null;
        if (!target || target === m.richMenuAliasId) return;
        const pair = `${m.id}>${target}`;
        if (seen.has(pair)) return;
        seen.add(pair);
        if (!byAlias.has(target)) {
          const n = {
            key: `x-${target}`,
            kind: aliasStatus !== 'ok' ? 'unchecked' : known.has(target) ? 'external' : 'unknown',
            label: aliasStatus !== 'ok' ? 'Not checked' : known.has(target) ? 'Other menu' : 'Missing alias', sub: target,
            col: extras.length % PER_ROW, row: memberRows + Math.floor(extras.length / PER_ROW),
          };
          extras.push(n);
          byAlias.set(target, n);
        }
        edges.push({ from: m.id, to: byAlias.get(target).key });
      });
    });
    return { nodes: [...nodes, ...extras], edges };
  }, [members, aliases, aliasStatus]);

  const { nodes, edges } = layout;
  const cols = Math.min(PER_ROW, Math.max(1, nodes.length));
  const rows = Math.max(1, ...nodes.map((n) => n.row + 1));
  const width = PAD * 2 + cols * W + (cols - 1) * GX;
  const height = PAD * 2 + rows * H + (rows - 1) * GY;
  const pos = new Map(nodes.map((n) => [n.key, { x: PAD + n.col * (W + GX) + W / 2, y: PAD + n.row * (H + GY) + H / 2 }]));
  const edgeSet = new Set(edges.map((e) => `${e.from}>${e.to}`));

  const style = {
    member: { fill: '#18181B', stroke: '#06C755', dash: undefined, text: '#FAFAFA' },
    external: { fill: '#09090B', stroke: '#71717A', dash: '5 4', text: '#D4D4D8' },
    unchecked: { fill: '#09090B', stroke: '#52525B', dash: '2 4', text: '#A1A1AA' },
    unknown: { fill: '#2A0F12', stroke: '#F87171', dash: '5 4', text: '#FCA5A5' },
  };

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4" data-testid="tab-map">
      <h2 className="font-semibold mb-3">Tab map</h2>
      {members.length === 0 ? (
        <p className="text-sm text-[#A1A1AA]">Add menus to see how their tabs link together.</p>
      ) : (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" style={{ maxWidth: width }} role="img" aria-label="Tab map">
          <defs>
            <marker id="tm-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0 0L10 5L0 10z" fill="#A1A1AA" />
            </marker>
            <marker id="tm-arrow-red" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0 0L10 5L0 10z" fill="#F87171" />
            </marker>
          </defs>
          {edges.map((e) => {
            const a = pos.get(e.from);
            const b = pos.get(e.to);
            const toNode = nodes.find((n) => n.key === e.to);
            const reverse = edgeSet.has(`${e.to}>${e.from}`);
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const len = Math.hypot(dx, dy) || 1;
            const off = reverse ? 16 : 0;
            const nx = (-dy / len) * off;
            const ny = (dx / len) * off;
            const p1 = edge(a.x + nx, a.y + ny, b.x + nx, b.y + ny);
            const p2 = edge(b.x + nx, b.y + ny, a.x + nx, a.y + ny);
            const red = toNode.kind === 'unknown';
            return (
              <line
                key={`${e.from}>${e.to}`}
                x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                stroke={red ? '#F87171' : '#A1A1AA'} strokeWidth="1.5"
                markerEnd={`url(#${red ? 'tm-arrow-red' : 'tm-arrow'})`}
                data-testid="tab-map-arrow"
              />
            );
          })}
          {nodes.map((n) => {
            const p = pos.get(n.key);
            const st = style[n.kind];
            return (
              <g key={n.key} data-testid={`tab-map-node-${n.kind}`}>
                <rect x={p.x - W / 2} y={p.y - H / 2} width={W} height={H} rx="10" fill={st.fill} stroke={st.stroke} strokeWidth="1.5" strokeDasharray={st.dash} />
                <text x={p.x} y={p.y - 4} textAnchor="middle" fontSize="13" fontWeight="600" fill={st.text}>
                  {n.star ? '★ ' : ''}{clip(n.label, 22)}
                </text>
                <text x={p.x} y={p.y + 14} textAnchor="middle" fontSize="11" fontFamily="monospace" fill={n.sub ? '#A1A1AA' : '#F87171'}>
                  {n.sub ? clip(n.sub, 26) : 'no alias'}
                </text>
              </g>
            );
          })}
        </svg>
      )}
      <p className="text-xs text-[#71717A] mt-3">
        Arrows show which tab each button switches to. Red = the alias doesn't exist.
      </p>
      {aliasStatus === 'error' && (
        <p className="text-xs text-amber-400 mt-1" data-testid="tab-map-unverified">
          Couldn't check LINE aliases — targets outside this campaign aren't verified.
        </p>
      )}
    </div>
  );
}
